// import React, { useEffect } from 'react';
// import { Box, CircularProgress, Typography } from '@mui/material';
// import { useNavigate, Outlet, useOutletContext, useLocation } from 'react-router-dom';
// import { gql, useQuery } from '@apollo/client';

// // ✨ FIX: Add the `includeCredits` field to the query
// const GET_BUDGETS_BY_TENANT = gql`
//   query BudgetsByTenant($tenantId: ID!) {
//     budgetsByTenant(tenantId: $tenantId) {
//       id
//       name
//       description
//       period
//       provider
//       amount
//       currentSpend
//       currentCredits
//       includeCredits
//       forecastedSpend
//       status
//       alertThresholds
//       tags
//       creator { name, photo }
//       scopeConfigurations {
//         id
//         dataSources { id, name }
//         regions
//         services
//         labels
//         k8sClusters
//         k8sNamespaces
//       }
//     }
//   }
// `;

// interface OutletContextType {
//   currentTenant: { id: string; name: string; } | null;
//   userRole?: string | null;
// }

// const BudgetAndAlerts: React.FC = () => {
//   const parentContext = useOutletContext<OutletContextType>();
//   const navigate = useNavigate();
//   const location = useLocation();

//   const { data, loading, error, refetch } = useQuery(GET_BUDGETS_BY_TENANT, {
//     variables: { tenantId: parentContext.currentTenant?.id },
//     skip: !parentContext.currentTenant?.id,
//     fetchPolicy: 'network-only',
//   });

//   useEffect(() => {
//     if (!loading && data) {
//       if (data.budgetsByTenant.length > 0 && location.pathname === '/budget-alerts') {
//         navigate('table', { replace: true });
//       }
//     }
//   }, [loading, data, navigate, location.pathname]);

//   if (loading) {
//     return (
//       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//         <CircularProgress />
//       </Box>
//     );
//   }
  
//   if (error) {
//     return (
//       <Box sx={{ p: 3, textAlign: 'center' }}>
//         <Typography color="error">Failed to load budgets. Please try again later.</Typography>
//       </Box>
//     );
//   }

//   return (
//     <Box sx={{
//       height: '100%',
//       p: { xs: 2, sm: 3 },
//       boxSizing: 'border-box',
//       display: 'flex',
//       flexDirection: 'column'
//     }}>
//       <Outlet context={{ ...parentContext, data, loading, error, refetch }} />
//     </Box>
//   );
// };

// export default BudgetAndAlerts;



import React from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useNavigate, Outlet, useOutletContext } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';

// GraphQL query to fetch budgets for a specific tenant.
const GET_BUDGETS_BY_TENANT = gql`
  query BudgetsByTenant($tenantId: ID!) {
    budgetsByTenant(tenantId: $tenantId) {
      id
      name
      description
      period
      provider
      amount
      currentSpend
      currentCredits
      includeCredits
      forecastedSpend
      status
      alertThresholds
      tags
      creator { name, photo }
      scopeConfigurations {
        id
        dataSources { id, name }
        regions
        services
        labels
        k8sClusters
        k8sNamespaces
      }
      metric
    }
  }
`;

// Component shown when no tenant is selected.
const NoTenantEmptyState: React.FC = () => (
    <Box
      sx={{ width: '100%', height: '100%', backgroundColor: '#FFFFFF', backdropFilter: 'blur(5px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 10, p: 2, textAlign: 'center', borderRadius: '10px' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img src="/icons/no-cost-analysis.svg" alt="No Tenant Selected" style={{ width: '150px', height: '150px', marginBottom: '12px' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontFamily:'Inter' }}>
            No Tenant Added
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb:2, maxWidth: '400px' ,fontFamily:'Inter',fontSize:'14px'}}>
            It looks like you haven't set up a tenant yet. Please add a tenant to set budget and alerts.
        </Typography>
      </Box>
    </Box>
);

// Component shown when a tenant exists but has no data sources.
const EmptyStateOverlay: React.FC<{ onNavigate: () => void, userRole?: string | null }> = ({ onNavigate, userRole }) => (
    <Box
      sx={{ width: '100%', height: '100%', backgroundColor: '#FFFFFF', backdropFilter: 'blur(5px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 10, p: 2, textAlign: 'center', borderRadius: '10px' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img src="/icons/No-data-src-added-log.svg" alt="No Data" style={{ width: '150px', height: '150px', marginBottom: '12px' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 , fontFamily:'Inter'}}>
            Ready to Get Your Budget Alerts?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb:2, maxWidth: '400px',fontFamily:'Inter',fontSize:'14px' }}>
            Please add a data source to your tenant to visualize alerts on spending and budgets
        </Typography>
        {userRole !== 'viewer' && (
          <Button disableElevation variant="contained" onClick={onNavigate} sx={{fontFamily:'Inter', backgroundColor:'#0064FF', fontSize: '14px', fontWeight: 500, borderRadius: '8px', textTransform: 'none' }}>
              Add New Data Source
          </Button>
        )}
      </Box>
    </Box>
);

// Defines the data passed down from the parent Dashboard route.
interface OutletContextType {
  currentTenant: { 
    id: string; 
    name: string; 
    dataSourceCount?: number;
  } | null;
  userRole?: string | null;
}

// The main layout component for the Budgets & Alerts page.
const BudgetAndAlerts: React.FC = () => {
  const parentContext = useOutletContext<OutletContextType>();
  const navigate = useNavigate();

  // This hook fetches budget data but correctly skips the request if there's
  // no tenant or if the tenant has no data sources.
  const { data, loading, error, refetch } = useQuery(GET_BUDGETS_BY_TENANT, {
    variables: { tenantId: parentContext.currentTenant?.id },
    skip: !parentContext.currentTenant?.id || parentContext.currentTenant.dataSourceCount === 0,
    fetchPolicy: 'network-only',
  });

  // Navigation handler for the "Add New Data Source" button.
  const navigateToAddDataSource = () => {
    if (parentContext.currentTenant?.id) {
        navigate('/data-sources/add', { state: { tenantId: parentContext.currentTenant.id } });
    }
  };

  // Determines what to render based on the current application state.
  const renderContent = () => {
    // State 1: No tenant is selected.
    if (!parentContext.currentTenant) {
        return <NoTenantEmptyState />;
    }

    // State 2: A tenant is selected, but it has no data sources.
    if (parentContext.currentTenant.dataSourceCount === 0) {
        return <EmptyStateOverlay onNavigate={navigateToAddDataSource} userRole={parentContext.userRole} />;
    }

    // State 3: Data is being fetched for a valid tenant.
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      );
    }
    
    // State 4: An error occurred during the data fetch.
    if (error) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">Failed to load budgets. Please try again later.</Typography>
        </Box>
      );
    }

    // Default State: Data has been fetched. Pass it down to the child route (TotalBudgetsPage).
    return <Outlet context={{ ...parentContext, data, loading, error, refetch }} />;
  };

  return (
    <Box sx={{ height: '100%', p: { xs: 2, sm: 3 }, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      {renderContent()}
    </Box>
  );
};

export default BudgetAndAlerts;